import { ControlCodes } from '@bcube/shared/constants';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { Control } from '@/api/admin/controls';
import { type Profile, profileApi } from '@/api/admin/profiles';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePermissions } from '@/hooks/use-permissions';
import { CONTROLS_TREE, type ControlNode } from '@/lib/controls-definition';
import { cn } from '@/lib/utils';
import { ControlDetailView } from './control-detail-view';
import { ProfileSelector } from './profile-selector';

// Hook for managing profile linked modal
function useProfileLinkedControlModal() {
  const [open, setOpen] = useState(false);
  const [selectedControl, setSelectedControl] = useState<Pick<
    Control,
    'id' | 'name' | 'code'
  > | null>(null);

  const { data: profiles, isLoading } = useQuery({
    queryKey: ['profiles-by-control', selectedControl?.code],
    queryFn: () => profileApi.findByControlCode(selectedControl?.code!),
    enabled: !!selectedControl?.code && open,
  });

  const handleControlSelection = useCallback((control: Pick<Control, 'id' | 'name' | 'code'>) => {
    setSelectedControl(control);
    setOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);

  return {
    open,
    control: selectedControl,
    profiles: profiles ?? { docs: [], total: 0, page: 1, pages: 1, limit: 10 },
    isLoading,
    handleControlSelection,
    closeModal,
  };
}

// Profile linked modal component
function ProfileLinkedControlModal({
  open,
  onClose,
  control,
  profiles,
  isLoading,
}: {
  open: boolean;
  onClose: () => void;
  control: Pick<Control, 'id' | 'name'> | null;
  profiles: { docs: Profile[] };
  isLoading: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Perfis relacionados ao controle de acesso - {control?.name}</DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }, (_, i) => `profile-skeleton-${i}`).map((key) => (
                <Skeleton key={key} className="h-12 w-full" />
              ))}
            </div>
          ) : profiles.docs.length === 0 ? (
            <p className="text-center text-gray-500 py-4">
              Nenhum perfil vinculado a este controle.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome do Perfil</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {profiles.docs.map((profile) => (
                  <TableRow key={profile.id}>
                    <TableCell className="font-medium">{profile.name}</TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2 py-1 text-xs font-medium',
                          profile.active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-700',
                        )}
                      >
                        {profile.active ? 'Ativo' : 'Inativo'}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ControlCard({
  node,
  onOpenProfileModal,
  canView,
}: {
  node: ControlNode;
  onOpenProfileModal: (control: Pick<Control, 'id' | 'name' | 'code'>) => void;
  canView?: boolean;
}) {
  return (
    <li className="group relative p-6 bg-white col-span-1 rounded-lg shadow ring-1 ring-inset ring-gray-900/10">
      <div>
        <h3 className="text-base font-semibold text-gray-900">{node.name}</h3>
        {canView && (
          <div className="w-fit mt-2 text-sm text-gray-400">
            <button
              type="button"
              className="text-gray-500 hover:underline hover:underline-offset-4 hover:text-blue-500 cursor-pointer bg-transparent border-0"
              onClick={() =>
                onOpenProfileModal({
                  id: node.code,
                  code: node.code,
                  name: node.name,
                })
              }
            >
              Ver Perfis
            </button>
          </div>
        )}
      </div>
    </li>
  );
}

export function ControlsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedProfileId = searchParams.get('profile_id') || '';
  const linkedControlModal = useProfileLinkedControlModal();
  const { hasPermission } = usePermissions();
  const canView = hasPermission(ControlCodes.admin_control_view);

  const handleProfileChange = (profileId: string) => {
    const params = new URLSearchParams(searchParams);
    if (profileId) {
      params.set('profile_id', profileId);
    } else {
      params.delete('profile_id');
    }
    setSearchParams(params);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
        <div>
          <h1 className="text-base font-semibold leading-7 text-gray-900">Controle de Acesso</h1>
          <p className="mt-2 text-sm text-gray-700">
            Tela para o gerenciamento de acessos que cada perfil pode fazer.
          </p>
        </div>
      </div>

      {/* Conditional render: detail view (includes its own ProfileSelector) or module cards */}
      {selectedProfileId ? (
        <div className="mt-10">
          <ControlDetailView profileId={selectedProfileId} onProfileChange={handleProfileChange} />
        </div>
      ) : (
        <>
          {/* Profile Selector */}
          <div className="mt-10 flex flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
            <div className="w-full ring-1 ring-inset ring-gray-900/10 rounded-md shadow-sm bg-white py-2 sm:py-4 lg:py-6 px-4 sm:px-6 lg:px-8">
              <ProfileSelector
                profileId={selectedProfileId}
                onProfileChange={handleProfileChange}
              />
            </div>
          </div>

          {/* Controls List (module cards) */}
          <div className="mt-10 px-4 sm:px-6 lg:px-8">
            <div className="p-4 sm:p-6 lg:p-8 ring-1 ring-inset ring-gray-900/10 rounded-md shadow-sm bg-white">
              <h1 className="text-base font-semibold leading-7 text-gray-900">
                Listagem dos módulos
              </h1>
              <div className="mt-4 border-b border-gray-900/10" />

              <ul
                aria-label="Módulos de controle"
                className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
              >
                {CONTROLS_TREE.map((node) => (
                  <ControlCard
                    key={node.code}
                    node={node}
                    onOpenProfileModal={linkedControlModal.handleControlSelection}
                    canView={canView}
                  />
                ))}
              </ul>
            </div>
          </div>

          {/* Profile Linked Modal */}
          <ProfileLinkedControlModal
            open={linkedControlModal.open}
            onClose={linkedControlModal.closeModal}
            control={linkedControlModal.control}
            profiles={linkedControlModal.profiles}
            isLoading={linkedControlModal.isLoading}
          />
        </>
      )}
    </div>
  );
}

export default ControlsPage;
